import React from 'react';

import { formatDateTime } from 'utils/dateUtils';
import {
  cleanEmailBody,
  formatAttachmentSize,
  formatEmailRecipient,
  formatEmailSender,
  hasAttachments,
  parseEmailLinks,
  parseEmailMessage,
} from 'utils/emailUtils';

import {
  AttachmentIcon,
  AttachmentInfo,
  AttachmentItem,
  AttachmentName,
  AttachmentSize,
  AttachmentsSection,
  EmailBody,
  EmailBodyLink,
  EmailBodyText,
  EmailContainer,
  EmailHeader,
  EmailHeaderLabel,
  EmailHeaderRow,
  EmailHeaderValue,
  EmailSubject,
} from './EmailMessage.styles';

/**
 * EmailMessage Component
 * Displays IMAP email messages in a properly formatted way
 */
const EmailMessage = ({ message, isMyMessage = false }) => {
  if (!message) return null;

  // Parse the email message
  const emailData = parseEmailMessage(message);

  if (!emailData) return null;

  const { subject, body, sender, recipient, date, attachments } = emailData;

  // Parse body to make links clickable
  const bodyParts = parseEmailLinks(cleanEmailBody(body));

  return (
    <EmailContainer $isMyMessage={isMyMessage}>
      {/* Email Subject */}
      {subject && <EmailSubject>{subject}</EmailSubject>}

      {!isMyMessage && (
        <EmailHeader>
          <EmailHeaderRow>
            <EmailHeaderLabel>From:</EmailHeaderLabel>
            <EmailHeaderValue>{formatEmailSender(sender)}</EmailHeaderValue>
          </EmailHeaderRow>

          <EmailHeaderRow>
            <EmailHeaderLabel>To:</EmailHeaderLabel>
            <EmailHeaderValue>{formatEmailRecipient(recipient)}</EmailHeaderValue>
          </EmailHeaderRow>

          <EmailHeaderRow>
            <EmailHeaderLabel>Date:</EmailHeaderLabel>
            <EmailHeaderValue>
              {formatDateTime(date, false, false, 'MMM D, YYYY [at] h:mm A')}
            </EmailHeaderValue>
          </EmailHeaderRow>
        </EmailHeader>
      )}

      {/* Email Body */}
      {body && (
        <EmailBody>
          <EmailBodyText>
            {bodyParts.map((part, index) => {
              if (part.type === 'link') {
                return (
                  <EmailBodyLink
                    key={index}
                    href={part.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {part.content}
                  </EmailBodyLink>
                );
              }
              return <span key={index}>{part.content}</span>;
            })}
          </EmailBodyText>
        </EmailBody>
      )}

      {/* Attachments */}
      {hasAttachments(message) && (
        <AttachmentsSection>
          <EmailHeaderLabel>Attachments ({attachments.length}):</EmailHeaderLabel>
          {attachments.map((attachment, index) => {
            const fileName =
              attachment.filename || attachment.name || `Attachment ${index + 1}`;
            const fileSize = attachment.size || 0;

            return (
              <AttachmentItem key={index}>
                <AttachmentIcon>📎</AttachmentIcon>
                <AttachmentInfo>
                  <AttachmentName
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                  >
                    {fileName}
                  </AttachmentName>
                  {fileSize > 0 && (
                    <AttachmentSize>{formatAttachmentSize(fileSize)}</AttachmentSize>
                  )}
                </AttachmentInfo>
              </AttachmentItem>
            );
          })}
        </AttachmentsSection>
      )}
    </EmailContainer>
  );
};

export default EmailMessage;
